// De keuzelijst achter 'Hoort bij' op een materiaal: aan welke interventie uit
// de meetstandaard telt dit product mee?
//
// WAAROM DIT TERUGKOMT. De koppeling liep ooit via een veld 'Interventiecode'.
// Dat is verwijderd omdat gebruikers een code niet konden plaatsen, met de
// bewuste acceptatie dat een zelfbedacht materiaal daarmee onkoppelbaar werd.
// In productie bleek dat 162 van de 268 registraties te raken: de koppeltabel
// matcht op exacte titel, en organisaties gebruiken hun eigen productnamen met
// artikelnummer erin ("Radiatorfolie 40cm (TZ410601)").
//
// De les was dat de PRESENTATIE fout was, niet de koppeling. Een naam kiezen kan
// een energiecoach; een code invullen niet. Deze lijst toont dus namen en
// bewaart de code.
// AI-CONTEXT: docs/inbox.md (bug #119)
//
// GEEN GOKKEN OP NAAM. Fuzzy matchen op genormaliseerde titels zou
// "Radiatorfolie 40cm (TZ410601)" goed raden en "Gordijnen" fout — en een fout
// bedrag is erger dan een gemeld gat, want een gat is zichtbaar.

/**
 * @typedef {import('../meetstandaard/adaptInterventieDocument').AdaptedInterventie} AdaptedInterventie
 *
 * @typedef {object} InterventionOption
 * @property {string} value - De interventiecode, wat er wordt opgeslagen
 * @property {string} label - De naam zoals de standaard hem schrijft
 *
 * @typedef {object} InterventionOptionGroup
 * @property {string} label - Groepskop: het activiteitstype van de standaard
 * @property {Array<InterventionOption>} options
 */

/**
 * Alle interventies die een kengetal hebben, gegroepeerd op activiteitstype.
 *
 * Interventies zonder kengetal blijven eruit: koppelen aan iets wat de standaard
 * niet kan becijferen levert een materiaal op dat er gekoppeld uitziet en toch
 * geen bedrag geeft — verwarrender dan niet gekoppeld.
 *
 * @param {{interventies?: Array<AdaptedInterventie>}|null} library
 * @returns {Array<InterventionOptionGroup>} Leeg zonder bibliotheek — nooit een verzonnen lijst
 */
const interventionOptions = (library) => {
    const interventies = Array.isArray(library?.interventies) ? library.interventies : []

    /** @type {Map<string, Array<InterventionOption>>} */
    const perGroep = new Map()
    interventies.forEach((i) => {
        if (!i || !i.code || i.status === 'no-kengetal') return
        const groep = i.activiteitstype || 'Overig'
        const opties = perGroep.get(groep) || []
        opties.push({ value: i.code, label: i.name || i.code })
        perGroep.set(groep, opties)
    })

    return [...perGroep.entries()]
        .map(([label, options]) => ({
            label,
            options: options.sort((a, b) => a.label.localeCompare(b.label, 'nl')),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'nl'))
}

/**
 * De naam bij een code, voor het tonen van een bestaande koppeling.
 *
 * @param {{byCode?: Object<string, AdaptedInterventie>}|null} library
 * @param {string} code
 * @returns {string} Lege string wanneer de code onbekend is — nooit de code zelf
 *   als naam, want dan lijkt jargon een productnaam
 */
const interventionNameFor = (library, code) => {
    if (!code || !library?.byCode) return ''
    return library.byCode[code]?.name || ''
}

export { interventionOptions, interventionNameFor }
